// Owns the save/draft/autosave lifecycle for the v2 planner: the dirty flag,
// the debounced localStorage draft, the debounced autosave, save/create,
// draft discovery on load (including the same-tab sign-in handoff), slug
// adoption after a rename, and the overview thumbnail. No DOM beyond
// localStorage/sessionStorage/fetch — everything user-facing (modals, the
// dirty CSS class, navigation) happens through `hooks`, so this is testable
// with a plain config object and a stubbed fetch/timers.
//
// `config` is a live view over the controller's Stimulus values (getters, and
// setters for the fields a rename can change: slug/saveUrl/dataUrl/imageUrl).
// A plain mutable object works just as well for tests.
//
// `hooks`: onDirtyChange(dirty), onSaveError(error, url) — error carries
// message/status/sessionExpired/isNetworkError, onDraftPersistFailed(),
// promptName() -> Promise<string|null>, promptGuestSignIn(), onRenamed(newBase),
// onCreated(uri), getSnapshotBlob() -> Promise<Blob|null>.

import {
  loadPlanData, savePlan, createPlan, saveDraft, saveDraftHandoff, loadDraft,
  loadDraftCandidate, clearDraft, clearDraftHandoff, csrfToken,
} from '../data';
import { toPlanPayload, toContent, toDraft } from '../serialize';

const AUTOSAVE_MS = 2000;
const DRAFT_MS = 400;
const OVERVIEW_THROTTLE_MS = 60000;

export default class SaveCoordinator {
  constructor(store, config, hooks = {}) {
    this.store = store;
    this.config = config;
    this.hooks = hooks;
    this._serverTimestamp = null;
    this._lastOverview = null;
    this._saveRejected = false;
  }

  // Guests draft under an empty username (`plans..new`) — a key no account can
  // own, so a draft left on a shared computer is only ever claimed through the
  // same-tab sign-in handoff, never by whoever signs in next.
  draftUser() { return this.config.username || ''; }
  draftSlug() { return this.config.slug || 'new'; }
  autoSaveEnabled() { return this.store.state.settings?.autoSave !== false; }
  // Autosave will pick up the next edit: an existing plan, the setting on, and
  // not paused after a rejected save.
  autoSaveActive() { return !this.config.isNew && !this._saveRejected && this.autoSaveEnabled(); }
  recordServerTimestamp(ts) { this._serverTimestamp = ts ?? null; }

  cancelTimers() { clearTimeout(this._autoT); clearTimeout(this._draftT); }

  markDirty() {
    this.store.state.dirty = true;
    this.hooks.onDirtyChange?.(true);
    clearTimeout(this._draftT);
    this._draftT = setTimeout(() => this.persistDraft(), DRAFT_MS);
    // New plans aren't autosaved (no record yet) — they're created on explicit
    // Save. The autoSave setting is a promise about data behaviour: when it's
    // off, nothing hits the server until the user saves (re-checked at fire
    // time in case the setting flipped during the debounce window). Autosave
    // also stays off after the server rejected a save, until an explicit save
    // succeeds, so one stale page can't retry in a loop.
    if (!this.config.isNew && !this._saveRejected && this.autoSaveEnabled()) {
      clearTimeout(this._autoT);
      this._autoT = setTimeout(() => { if (this.autoSaveEnabled()) this.save(); }, AUTOSAVE_MS);
    }
  }

  // Write the draft now (cancelling the debounce). Returns false — after
  // telling the user — when the browser refused to store it.
  persistDraft() {
    clearTimeout(this._draftT);
    if (saveDraft(this.draftUser(), this.draftSlug(), toDraft(this.store.state))) return true;
    this.hooks.onDraftPersistFailed?.();
    return false;
  }

  // Before a guest leaves for the sign-in page: keep the draft under the guest
  // key and hand a token-scoped copy to this tab's post-sign-in page load.
  startSignInHandoff() {
    const draft = toDraft(this.store.state);
    if (saveDraft(this.draftUser(), this.draftSlug(), draft) && saveDraftHandoff(draft)) return true;
    this.hooks.onDraftPersistFailed?.();
    return false;
  }

  setSaving(on) { this.saving = on; this.hooks.onSavingChange?.(on); }

  async save() {
    clearTimeout(this._autoT);
    if (this.config.isNew) return this.saveNewPlan();
    this.setSaving(true);
    try {
      const res = await savePlan(this.config.saveUrl, toPlanPayload(this.store.state));
      this.store.state.dirty = false;
      this._saveRejected = false;
      this.lastSaveFailed = false;
      this.setSaving(false);
      this.hooks.onDirtyChange?.(false);
      clearDraft(this.draftUser(), this.config.slug);
      if (res.timestamp) this._serverTimestamp = res.timestamp;
      this.adoptRenamedSlug(res.uri);
      this.captureOverview();
      return res;
    } catch (e) {
      // A save the server rejected (e.g. an expired page session) keeps failing
      // until the user reloads, so halt autosave to stop a burst of identical
      // errors. A network rejection is transient, so leave autosave running to
      // retry when the connection returns.
      if (!e.isNetworkError) this._saveRejected = true;
      this.lastSaveFailed = true;
      this.setSaving(false);
      this.hooks.onSaveError?.(e, this.config.saveUrl);
      return null;
    }
  }

  async saveNewPlan() {
    if (!this.config.username) {
      if (this.persistDraft()) this.hooks.promptGuestSignIn?.();
      return null;
    }
    let name = this.store.state.settings?.name;
    if (!name || !name.trim()) {
      name = await this.hooks.promptName?.();
      if (name == null) return null; // cancelled
      this.store.state.settings = { ...this.store.state.settings, name: name.trim() };
    }
    try {
      const res = await createPlan(this.config.createUrl, toPlanPayload(this.store.state));
      this.store.state.dirty = false;
      this.hooks.onDirtyChange?.(false);
      clearDraft(this.draftUser(), this.draftSlug());
      this.hooks.onCreated?.(res.uri);
      return res;
    } catch (e) {
      this.hooks.onSaveError?.(e, this.config.createUrl);
      return null;
    }
  }

  // Renaming a plan changes its slug server-side; follow the uri the save
  // response returns so later saves/loads/uploads don't hit the stale slug.
  adoptRenamedSlug(uri) {
    const m = String(uri || '').match(/^\/([^/]+)\/plans\/([^/]+)\/edit$/);
    if (!m || m[2] === this.config.slug) return;
    const base = `/${m[1]}/plans/${m[2]}`;
    this.config.slug = m[2];
    this.config.saveUrl = base;
    this.config.dataUrl = `${base}/data`;
    this.config.imageUrl = `${base}/image`;
    this.hooks.onRenamed?.(base);
  }

  async loadPlan() {
    const plan = await loadPlanData(this.config.dataUrl);
    this.recordServerTimestamp(plan.timestamp ?? null);
    return plan;
  }

  // Look for a localStorage draft worth offering to restore: a fresh same-tab
  // sign-in handoff first, then this account's own draft. Returns
  // { draft, handoff, handoffToken } or null. Stale drafts (at or behind the
  // server's timestamp) and drafts identical to what's loaded are cleared
  // quietly — the caller only needs to react to a real offer.
  findDraft() {
    const candidate = loadDraftCandidate(this.config.username, this.draftSlug());
    if (candidate) return this.vetDraft(candidate);
    return null;
  }

  // The account draft alone — what's left to offer once a handoff is settled.
  findAccountDraft() {
    const draft = loadDraft(this.draftUser(), this.draftSlug());
    return draft ? this.vetDraft({ draft, handoff: false, handoffToken: null }) : null;
  }

  vetDraft(found) {
    const { draft } = found;
    // A draft at or behind the server's timestamp is stale (e.g. the post-save
    // draft clear raced a crash) — discard it instead of nagging.
    const stale = draft?.timestamp && this._serverTimestamp && draft.timestamp <= this._serverTimestamp;
    let same = false;
    try {
      // Compare content, plus the plan's name and access when the draft carries
      // them (they live at the plan level, outside content — a rename alone is
      // still unsaved work). Species/timestamp are caches, never compared.
      const state = this.store.state;
      const sameField = (key) => draft.settings?.[key] == null || draft.settings[key] === state.settings?.[key];
      same = JSON.stringify(toContent(draft)) === JSON.stringify(toContent(state))
        && sameField('name') && sameField('access');
    } catch (_) { /* compare best-effort */ }
    if (!stale && !same) return found;
    return this.discardDraft(found);
  }

  // The user chose Restore. A handoff draft is first moved to this account's
  // key (so a reload can't lose it); returns false if the browser refused.
  acceptDraft(found) {
    if (!found.handoff) return true;
    if (!saveDraft(this.draftUser(), this.draftSlug(), found.draft)) {
      this.hooks.onDraftPersistFailed?.();
      return false;
    }
    clearDraftHandoff(found.handoffToken);
    return true;
  }

  // The user chose Discard (or the draft was stale/identical). Discarding a
  // handoff falls through to the account's own draft, which is returned (vetted)
  // so the caller can offer it next.
  discardDraft(found) {
    if (found.handoff) {
      clearDraftHandoff(found.handoffToken);
      return this.findAccountDraft();
    }
    clearDraft(this.draftUser(), this.draftSlug());
    return null;
  }

  // Best-effort overview thumbnail: snapshot the canvas and POST it as the
  // plan's `overview` image (shown on the plans index). Throttled to once a
  // minute and skipped for new/unsaved plans; failures are non-fatal.
  async captureOverview() {
    if (this.config.isNew || !this.config.imageUrl) return;
    const now = Date.now();
    if (this._lastOverview && now - this._lastOverview < OVERVIEW_THROTTLE_MS) return;
    this._lastOverview = now;
    try {
      const blob = await this.hooks.getSnapshotBlob?.();
      if (!blob) return;
      const form = new FormData();
      form.append('image[file]', blob, 'overview.png');
      await fetch(`${this.config.imageUrl}/overview`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: form,
      });
    } catch (_) { /* thumbnail is non-critical */ }
  }
}
